import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Layout from "./Layout";
import {
  Plus,
  Search,
  MapPin,
  Zap,
  TrendingUp,
  DollarSign,
  Eye,
  Edit,
  Trash2,
  X,
  Cpu,
  Layers,
  AlertTriangle,
} from "lucide-react";
import {
  fetchAdminProjects,
  deleteAdminProject,
} from "../Services/userServices";

const Projects = () => {
  const [projectsList, setProjectsList] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadProjects = async () => {
    setIsLoading(true);
    setErrorMessage("");
    const filters = { search: searchQuery };
    const statusFilters = ["Completed", "In Progress", "Pending"];
    const categoryFilters = [
      "Residential",
      "Commercial",
      "Industrial",
      "Agriculture",
    ];
    if (statusFilters.includes(activeFilter)) filters.status = activeFilter;
    else if (categoryFilters.includes(activeFilter))
      filters.category = activeFilter;
    const result = await fetchAdminProjects(filters);
    if (result.success) setProjectsList(result.data);
    else setErrorMessage(result.error);
    setIsLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProjects();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, activeFilter]);

  const confirmDeleteTrigger = (id) => setDeleteConfirmId(id);

  const executeDelete = async () => {
    if (!deleteConfirmId) return;
    const result = await deleteAdminProject(deleteConfirmId);
    if (result.success)
      setProjectsList((prev) => prev.filter((p) => p.id !== deleteConfirmId));
    else alert(result.error || "Failed to delete.");
    setDeleteConfirmId(null);
  };

  return (
    <Layout>
      <div className="space-y-8 pb-12">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
              Projects Management
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Manage, publish and control your solar installations portfolio
            </p>
          </div>
          <Link
            to="/admin/projects/add"
            className="px-5 py-2.5 bg-[#10B981] hover:bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all text-xs flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add New Project</span>
          </Link>
        </div>

        {/* SEARCH & FILTERS */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, customer, city..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-xs font-medium"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            {[
              "All",
              "Completed",
              "In Progress",
              "Pending",
              "Residential",
              "Commercial",
              "Industrial",
              "Agriculture",
            ].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeFilter === filter
                    ? "bg-slate-900 text-white dark:bg-emerald-500 dark:text-white shadow-sm"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700/60"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* ERROR */}
        {errorMessage && (
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* LOADING / EMPTY / GRID */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projectsList.length === 0 ? (
          <div className="p-12 text-center bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
            <Layers className="w-10 h-10 text-slate-400 mx-auto mb-3 opacity-60" />
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
              No Projects Found
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Try adjusting your search or filter
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectsList.map((project) => (
              <div
                key={project.id}
                className="bg-white dark:bg-slate-900/50 rounded-2xl shadow-sm dark:shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden hover:shadow-lg dark:hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="h-48 overflow-hidden relative bg-slate-100 dark:bg-slate-800">
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Layers className="w-10 h-10 opacity-40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <span
                      className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider rounded-full backdrop-blur-md ${
                        project.status === "Completed"
                          ? "bg-emerald-500/90 text-white"
                          : project.status === "In Progress"
                            ? "bg-amber-500/90 text-white"
                            : "bg-slate-700/90 text-slate-200"
                      }`}
                    >
                      {project.status}
                    </span>
                    <span className="absolute bottom-3 left-3 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-white/90 dark:bg-slate-900/90 text-slate-900 dark:text-white">
                      {project.tag}
                    </span>
                  </div>
                  <div className="p-5 space-y-3">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-1">
                        {project.title}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                        Client:{" "}
                        <span className="font-semibold text-slate-700 dark:text-slate-300">
                          {project.customer}
                        </span>
                      </p>
                    </div>
                    <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-3">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-500">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />{" "}
                          Location
                        </span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {project.location}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-500">
                          <Zap className="w-3.5 h-3.5 text-amber-500" />{" "}
                          Capacity
                        </span>
                        <span className="font-bold text-slate-900 dark:text-white">
                          {project.capacity}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-500">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />{" "}
                          Savings
                        </span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                          {project.savings}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-500">
                          <DollarSign className="w-3.5 h-3.5 text-blue-500" />{" "}
                          Cost
                        </span>
                        <span className="font-bold text-slate-800 dark:text-slate-200">
                          {project.amount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3.5 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                  <button
                    onClick={() => setSelectedProject(project)}
                    className="flex-1 py-2 px-3 text-xs font-bold bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl border border-slate-200 dark:border-slate-700/60 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>Quick View</span>
                  </button>
                  <Link
                    to={`/admin/projects/edit/${project.id}`}
                    title="Edit"
                    className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl border border-emerald-500/20 transition-all"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => confirmDeleteTrigger(project.id)}
                    title="Delete"
                    className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-xl border border-red-500/20 transition-all cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QUICK VIEW MODAL — Portal */}
      {selectedProject &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest rounded-md bg-emerald-500/10 text-[#10B981] border border-emerald-500/20">
                    {selectedProject.tag}
                  </span>
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1.5">
                    {selectedProject.title}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Client: {selectedProject.customer} •{" "}
                    {selectedProject.location}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {selectedProject.image && (
                <div className="rounded-2xl overflow-hidden aspect-[16/9] bg-slate-100 dark:bg-slate-800">
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Project Scope
                </h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Cpu className="w-3.5 h-3.5 text-[#10B981]" />
                  <span>Technical Specs</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">
                      Panels:
                    </span>
                    <strong className="text-slate-800 dark:text-slate-200">
                      {selectedProject.panels || "N/A"}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">
                      Inverter:
                    </span>
                    <strong className="text-slate-800 dark:text-slate-200">
                      {selectedProject.inverter || "N/A"}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">
                      Battery:
                    </span>
                    <strong className="text-slate-800 dark:text-slate-200">
                      {selectedProject.battery || "N/A"}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">
                      Roof:
                    </span>
                    <strong className="text-slate-800 dark:text-slate-200">
                      {selectedProject.roof_type || "N/A"}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-center">
                <div>
                  <span className="text-[10px] font-bold uppercase text-slate-400 block">
                    Capacity
                  </span>
                  <strong className="text-base font-black text-slate-900 dark:text-white">
                    {selectedProject.capacity}
                  </strong>
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase text-slate-400 block">
                    Savings
                  </span>
                  <strong className="text-base font-black text-emerald-600 dark:text-emerald-400">
                    {selectedProject.savings}
                  </strong>
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase text-slate-400 block">
                    Cost
                  </span>
                  <strong className="text-base font-black text-slate-900 dark:text-white">
                    {selectedProject.amount}
                  </strong>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Link
                  to={`/admin/projects/edit/${selectedProject.id}`}
                  className="px-5 py-2.5 rounded-xl bg-[#10B981] hover:bg-emerald-600 text-white font-bold text-xs transition-all flex items-center gap-1.5"
                >
                  <Edit className="w-3.5 h-3.5" />
                  <span>Edit Full Details</span>
                </Link>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs transition-all cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* DELETE CONFIRMATION — Portal */}
      {deleteConfirmId &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl max-w-sm w-full shadow-2xl text-center space-y-5">
              <div className="w-12 h-12 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto border border-red-500/20">
                <Trash2 className="w-5 h-5 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  Delete Project?
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Are you sure you want to permanently delete this project? This
                  action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDelete}
                  className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-md shadow-red-500/20 transition-all cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Layout>
  );
};

export default Projects;
